// subscription-plan.component.ts - FIXED VERSION
import { Component, OnInit, OnDestroy } from '@angular/core';
import { StripeService } from '../../../services/stripe.service';
import { loadStripe } from '@stripe/stripe-js';
import { environment } from '../../../../environments/environment';
import { UserService } from '../../../services/user.service';
import { ToastService } from '../../../services/toast.service';
import { AuthService } from '../../../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SubscriptionService } from '../../../services/subscription.service';
import { StorageService } from '../../../services/storage.service';

interface SubscriptionPlan {
  _id: string;
  name: string;
  price: number;
  currency: string;
  price_id: string;
  duration: number;
  features?: any[];
  upgradeOrder: number;
  displayFeatures?: any[];
  cancel_at_period_end?: boolean;
  scheduled_cancel_date?: Date;
  isFreeTrial?: boolean;
  is_active?: boolean;
  subscription_status?: string;
  was_free_trial?: boolean;
  pricing?: {
    monthly: {
      original: number;
      upgrade_order: number;
      discounted: number;
      price_id: string;
    };
    yearly: {
      original: number;
      upgrade_order: number;
      discounted: number;
      price_id: string;
    };
  };
}

interface PaymentForm {
  agreeTerms: boolean;
}

@Component({
  selector: 'subscription-plan',
  templateUrl: './subscription-plan.component.html',
  styleUrls: ['./subscription-plan.component.scss'],
})
export class SubscriptionPlanComponent implements OnInit, OnDestroy {
  stripe: any;
  elements: any;
  paymentElement: any;
  plans: SubscriptionPlan[] = [];
  filteredPlans: SubscriptionPlan[] = [];
  activePlan: SubscriptionPlan | null = null;
  userId = '';
  user: any | null = null;
  url: string | null = null;

  showConfirmationDialog = false;
  selectedPlan: SubscriptionPlan | null = null;
  isUpgrade = false;
  isDowngrade = false;
  returnUrl: string | null = null;

  showCancelDialog = false;
  isCancelling = false;
  freeTrialCancelled = false;
  isEligibleForFreeTrial: boolean = false;
  showCancelledTrialBanner: boolean = false;
  showPaymentModal = false;
  session: any; // Will hold Stripe session object with clientSecret
  selectedBillingPeriod: 'monthly' | 'yearly' = 'monthly';

   offerDate = environment.OFFER_DARTE;


  constructor(
    private stripeService: StripeService,
    private userService: UserService,
    private subscriptionService: SubscriptionService,
    private toaster: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private storageService: StorageService,
  ) { }

  async ngOnInit(): Promise<void> {
    this.showCancelledTrialBanner =
      localStorage.getItem('showCancelledTrialBanner') === 'true';
    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    this.user = this.authService.getUser();
    this.fetchParams();
    // this.initializeStripe();
    this.fetchSubscriptionPlans();
    this.getActiveSubscription();
    this.checkTrialEligibility();
  }

  getDiscountedPercentage(plan: SubscriptionPlan): number {
    if (this.selectedBillingPeriod === 'yearly') {
      const yearly = plan.pricing?.yearly;
      if (yearly) {
        const discount = yearly.original - yearly.discounted;
        return Math.round((discount / yearly.original) * 100);
      }
    } else {
      const monthly = plan.pricing?.monthly;
      if (monthly) {
        const discount = monthly.original - monthly.discounted;
        return Math.round((discount / monthly.original) * 100);
      }
    }
    return 0;
  }



  setBillingPeriod(period: 'monthly' | 'yearly'): void {
    this.selectedBillingPeriod = period;
  }

  checkTrialEligibility(): void {
    this.stripeService.checkTrialEligibility().subscribe({
      next: (res: any) => {
        this.isEligibleForFreeTrial = res.eligible;
        console.log('Trial eligibility:', this.isEligibleForFreeTrial);
      },
      error: () => {
        this.isEligibleForFreeTrial = false;
      },
    });
  }

  getActiveSubscription(): void {
    this.subscriptionService.getSubscriptionObservable().subscribe({
      next: (response: any) => {
        // FIX: Only set activePlan if subscription is actually active
        if (response && response.is_active === true) {
          this.activePlan = {
            ...response.subscription_plan,
            isFreeTrial: response.subscription_plan?.isFreeTrial,
            price_id: response?.price_id,
            cancel_at_period_end: response.cancel_at_period_end,
            scheduled_cancel_date: response.scheduled_cancel_date,
            subscription_status: response.subscription_status,
            is_active: response.is_active,
            was_free_trial: response.was_free_trial,
          };
        } else {
          // FIX: Clear activePlan if subscription is not active
          this.activePlan = null;
        }
      },
      error: () => {
        this.activePlan = null;
      },
    });
  }

  filterPlans(): void {
    this.filteredPlans = this.plans;
  }

  fetchParams(): void {
    this.url = this.route.snapshot.queryParamMap.get('url');
  }

  async initializeStripe(clientSecret: string): Promise<void> {
    this.stripe = await loadStripe(environment.STRIPE_PUBLISHABLE_KEY);
    this.elements = this.stripe.elements({
      clientSecret,
    });
    this.paymentElement = this.elements.create('checkout');
    this.paymentElement.mount('#payment-element');
  }

  fetchSubscriptionPlans(): void {
    this.stripeService.getAllSubscriptions().subscribe({
      next: (response: any) => {
        console.log('Fetched plans:', response);
        this.plans = response;
        this.filterPlans();
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  startFreeTrial(): void {
    this.userService.startFreeTrial().subscribe({
      next: (response: any) => {
        this.authService.setUser(response.user);
        this.authService.setSubscription(response.subscription);
        this.subscriptionService.refreshSubscription();
        this.toaster.showToast('Free Trial Activated!', 'success');
        this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
        this.router.navigateByUrl(this.returnUrl || '/dashboard');
      },
      error: (error) => {
        console.error('Error starting free trial:', error);
        this.toaster.showToast(
          'Failed to start free trial. Please try again.',
          'error'
        );
      },
    });
  }

  selectPlan(plan: SubscriptionPlan): void {
    let priceId = plan.pricing?.monthly?.price_id;
    if (this.selectedBillingPeriod === 'yearly') {
      priceId = plan.pricing?.yearly?.price_id;
    }
    else {
      priceId = plan.pricing?.monthly?.price_id;
    }

    this.stripeService.createCustomerPortalSession(this.user.email, 'subscription_update_confirm', priceId??'').subscribe({
      next: (session) => {
        console.log('Session:', session.url.url);
        window.location.href = session.url.url;
      },
      error: (error) => {
        console.error('Error creating customer portal session:', error);
        this.toaster.showToast('Error processing request', 'error');
      },
    });
    return;
    // this.selectedPlan = plan;
    // console.log(
    //   'Selected plan:',
    //   this.selectedPlan,
    //   'Active plan:',
    //   this.activePlan
    // );
    // if (this.activePlan) {
    //   this.isUpgrade = plan.upgradeOrder > this.activePlan.upgradeOrder;
    //   this.isDowngrade = plan.upgradeOrder < this.activePlan.upgradeOrder;
    //   this.showConfirmationDialog = true;
    // } else {
    //   // Subscribe for the first time
    //   this.purchasePlan(this.selectedPlan);
    // }
  }

  getButtonText(plan: SubscriptionPlan): string {
    if (!this.activePlan || this.activePlan.isFreeTrial) return 'Subscribe';

    let activePriceId = this.activePlan?.price_id;
    let activeUpgradeOrder = 0;
    if (this.activePlan?.pricing?.monthly.price_id === activePriceId) {
      activeUpgradeOrder = this.activePlan?.pricing?.monthly.upgrade_order;
    }
    else if (this.activePlan?.pricing?.yearly.price_id === activePriceId) {
      activeUpgradeOrder = this.activePlan?.pricing?.yearly.upgrade_order;
    }
    // if(plan.pricing?.yearly?.upgradeOrder===6)
    // console.log("activeUpgradeOrder -->", activeUpgradeOrder, plan.pricing?.yearly?.upgradeOrder)
    if (this.selectedBillingPeriod === 'yearly') {
      if (plan.pricing?.yearly?.price_id === this.activePlan.price_id && plan._id === this.activePlan._id) {
        console.log("Executed line [234] --> ", plan.pricing?.yearly?.price_id === this.activePlan.price_id);
        return 'Current Plan';
      }
      if ((plan.pricing?.yearly?.upgrade_order ?? 0) > (activeUpgradeOrder ?? 0)) {
        console.log("Executed line [238] --> ", plan.pricing?.yearly?.upgrade_order, this.activePlan.pricing?.yearly.upgrade_order);
        return 'Upgrade';
      }





      if ((plan.pricing?.yearly.upgrade_order ?? 0) < (activeUpgradeOrder ?? 0)) {
        console.log("Executed line [243] --> ", plan.pricing?.yearly.upgrade_order, this.activePlan.pricing?.yearly.upgrade_order);
        return 'Downgrade';
      }

    }
    else if (this.selectedBillingPeriod === 'monthly') {

      if (plan.pricing?.monthly?.price_id === this.activePlan?.price_id && this.activePlan && plan._id === this.activePlan._id) {
        console.log("Executed line [250] --> ", plan.pricing?.monthly?.price_id, this.activePlan?.price_id);
        return 'Current Plan';
      }

      if ((plan.pricing?.monthly.upgrade_order ?? 0) > (activeUpgradeOrder ?? 0)) {
        console.log("Executed line [] --> ",);
        return 'Upgrade';
      }

      if ((plan.pricing?.monthly?.upgrade_order ?? 0) < (activeUpgradeOrder ?? 0)) {
        console.log("Executed line [] --> ",);
        return 'Downgrade';
      }
    }



    return 'Subscribe';
  }

  getButtonClass(plan: SubscriptionPlan): string {
    if (!this.activePlan) return 'purchase-button';
    if (this.getButtonText(plan) === 'Current Plan') return 'purchase-button current-plan';
    if (this.activePlan.isFreeTrial) return 'purchase-button';
    if (this.getButtonText(plan) === 'Upgrade')
      return 'purchase-button upgrade';
    if (this.getButtonText(plan) === 'Downgrade')
      return 'purchase-button downgrade';
    return 'purchase-button';
  }

  isCurrentPlan(plan: SubscriptionPlan): boolean {
    if (this.selectedBillingPeriod === 'yearly') {
      return this.activePlan ? plan._id === this.activePlan._id
        && plan.pricing?.yearly?.price_id === this.activePlan?.price_id : false;
    }
    else {
      return this.activePlan ? plan._id === this.activePlan._id
        && plan.pricing?.monthly?.price_id === this.activePlan?.price_id : false;
    }

  }

  isFreeTrial(): boolean {
    return (
      !!this.activePlan &&
      (this.activePlan.subscription_status === 'trial' ||
        this.activePlan.was_free_trial === true)
    );
  }

  isPaidPlan(): boolean {
    return (
      !!this.activePlan && this.activePlan.price > 0 && !this.isFreeTrial()
    );
  }

  canCancelSubscription(): boolean {
    return (
      !!this.activePlan &&
      this.activePlan.is_active === true &&
      (this.activePlan.isFreeTrial === true ||
        this.activePlan.cancel_at_period_end === false)
    );
  }

  isCancelledButActive(): boolean {
    if (!this.activePlan) return false;

    // Show banner if trial was cancelled
    if (
      this.isFreeTrial() &&
      this.activePlan.is_active === false &&
      this.activePlan.cancel_at_period_end
    ) {
      return true;
    }

    // Show banner if paid plan is scheduled for cancellation
    return (
      this.activePlan.cancel_at_period_end === true &&
      this.activePlan.is_active === true
    );
  }

  confirmPlanChange(): void {
    if (this.selectedPlan) {
      // this.updatePlan(this.selectedPlan);
      this.purchasePlan(this.selectedPlan);
      this.closeConfirmationDialog();
    }
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedPlan = null;
    this.isUpgrade = false;
    this.isDowngrade = false;
  }

  showCancelConfirmation(): void {
    this.showCancelDialog = true;
  }

  closeCancelDialog(): void {
    this.showCancelDialog = false;
  }

  // FIX: Updated cancel subscription method
  async confirmCancelSubscription(): Promise<any> {
    if (this.isCancelling) return;

    this.isCancelling = true;

    this.stripeService.cancelSubscription().subscribe({
      next: (response: any) => {
        if (this.isFreeTrial()) {
          this.storageService.setShowCancelledTrialBanner(true);
          this.showCancelledTrialBanner = true;
          // FIX: For free trial, completely clear the subscription
          this.activePlan = null;
          this.subscriptionService.clearSubscription();
          this.toaster.showToast(
            'Free trial cancelled successfully.',
            'success'
          );
        } else {
          // For paid plans, mark as scheduled for cancellation
          if (this.activePlan) {
            this.activePlan.cancel_at_period_end = true;
            this.activePlan.scheduled_cancel_date = response.endDate;
          }
          this.toaster.showToast(
            'Subscription cancelled. It will remain active until the end of your billing period.',
            'success'
          );
        }

        this.closeCancelDialog();
        this.isCancelling = false;

        // FIX: Only refresh subscription for paid plans
        if (!this.isFreeTrial()) {
          this.subscriptionService.refreshSubscription();
        }
      },
      error: (error) => {
        console.error('Error cancelling subscription:', error);
        this.toaster.showToast(
          error.error?.error ||
          'Failed to cancel subscription. Please try again.',
          'error'
        );
        this.isCancelling = false;
      },
    });
  }

  formatDate(date: string | Date | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  updatePlan(plan: SubscriptionPlan): void {
    this.stripeService.updatePlan(plan._id).subscribe({
      next: (response) => {
        this.session = response;
        this.showPaymentModal = true; // This triggers child modal open and Stripe initialization
        console.log('Response:', response.url);
        //  window.location.href = response.url;
      },
      error: (error) => {
        console.error('Error creating checkout session:', error);
        this.toaster.showToast('Error processing request', 'error');
      },
    });
  }
  closePaymentModal() {
    this.showPaymentModal = false;
  }

  handlePaymentSuccess() {
    this.showPaymentModal = false;
    this.toaster.showToast('Payment successful!', 'success');
    // // Refresh subscription after successful payment
    // this.subscriptionService.refreshSubscription();
  }

  // Example stub for backend call
  async createStripeSessionForUpgrade() {
    // Call your backend API that updates subscription with always_invoice and returns clientSecret
    // Example:
    // const res = await fetch('/api/subscription/upgrade', { method: 'POST' });
    // return await res.json();

    // Placeholder:
    return { clientSecret: 'pi_abc_secret_123' };
  }

  purchasePlan(plan: SubscriptionPlan): void {
    const selectedPriceId =
      this.selectedBillingPeriod === 'monthly'
        ? plan.pricing?.monthly?.price_id
        : plan.pricing?.yearly?.price_id;

    this.stripeService
      .createCheckoutSession(selectedPriceId, this.returnUrl)
      .subscribe({
        next: (session) => {
          console.log('Session:', session.url);
          window.location.href = session.url;
        },
        error: (error) => {
          console.error('Error creating checkout session:', error);
          if (error.status == 406) {
            this.toaster.showToast(
              'You already have an active subscription for this plan.',
              'success'
            );
            this.subscriptionService.setSubcription(error.error.data);
            this.router.navigateByUrl('/dashboard');
          } else {
            this.toaster.showToast('Error processing request', 'error');
          }
        },
      });
  }

  ngOnDestroy(): void { }
}
